/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';

export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: Array<CodeBlock> = [];

    const imageWidgets = [];
    const textWidgets = [];
    const LCDMenuFeatureEnabled = (feature as any).getSettingsEnabled();
    const screens = feature.getSettingValue('SCREENS');

    const { project } = feature;
    const BACKCOLOR = PlatformUtils.hexToTibboColor(feature.getSettingValue('BACKCOLOR'));
    const FORECOLOR = PlatformUtils.hexToTibboColor(feature.getSettingValue('FORECOLOR'));
    const LOGFONT = feature.getSettingValue('LOGFONT');
    const SCREEN_WIDTH = 320;
    const SCREEN_HEIGHT = 240;

    for (let j = 0; j < screens.length; j++) {
        const screen = screens[j];
        const widgets = screen.getSettingValue('WIDGETS');
        for (let i = 0; i < widgets.length; i++) {
            const widget = widgets[i];
            const type = widget.id;
            switch (type) {
                case 'lcd_image_widget':
                    imageWidgets.push(widget);
                    break;
                case 'lcd_text_widget':
                    textWidgets.push(widget);
                    break;
                default:
                    break;
            }
        }

        for (let i = 0; i < widgets.length; i++) {
            const widget = widgets[i];
            blocks.push(...widget.getCodeBlocks());
        }

        if (LCDMenuFeatureEnabled && widgets.length > 0) {
            blocks.push({
                location: 'ui_menu_exit',
                blockType: BlockType.CODE,
                template: `
    lcd_display_widgets(display_current_screen)`,
            });
        }
    }

    const fontHeight = LOGFONT === 'LCD_STD_FONT' ? 19 : 8;
    const fontWidth = LOGFONT === 'LCD_STD_FONT' ? 11 : 5;
    const totalLines = Math.floor(SCREEN_HEIGHT / fontHeight) - 1;
    const stringSize = Math.floor(SCREEN_WIDTH / fontWidth);
    const maxStringSize = Math.floor(SCREEN_WIDTH / (fontWidth + 1));
    blocks.push({
        location: 'DEVICE_TBS_DIMS',
        blockType: BlockType.CODE,
        template: `
dim lcd_logs(${totalLines}) as string(${stringSize})
dim lcd_log_index as byte=0`,
    });

    blocks.push({
        location: 'GLOBAL_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
declare sub lcd_log(text as string)`,
    });

    blocks.push({
        location: 'GLOBAL_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
const LOG_FONT_FILE = ${LOGFONT}`,
    });

    blocks.push({
        location: 'DEVICE_TBS',
        blockType: BlockType.CODE,
        template: `
sub lcd_log(text as string)
    dim i as byte
    if lcd_log_index < ${totalLines} - 1 then
        lcd_log_index = lcd_log_index + 1
    else 
        for i=0 to ${totalLines} - 2
            lcd_logs(i) = lcd_logs(i + 1)
        next i
    end if
    lcd_logs(lcd_log_index) = left(text,${maxStringSize})
    romfile.open(LOG_FONT_FILE)
    lcd.setfont(romfile.offset)
    lcd.lock()
    lcd.forecolor=${BACKCOLOR}
    lcd.fill(0,0,lcd.width,lcd.height)
    lcd.forecolor=${FORECOLOR}
    for i=0 to ${totalLines} - 1
        lcd.print(lcd_logs(i),0,i * ${fontHeight})
    next i
    lcd.unlock()
end sub`,
    });

    blocks.push({
        location: 'GLOBAL_DEFINES',
        blockType: BlockType.CODE,
        template: `
#define LCD_IMG_WIDGETS_COUNT ${imageWidgets.length}
#define LCD_TEXT_WIDGETS_COUNT ${textWidgets.length}`,
    });


    for (let i = 0; i < imageWidgets.length; i++) {
        const widget = imageWidgets[i];
        const widgetObject = widget.getWidgetObject();
        blocks.push({
            location: 'lcd_init_widgets',
            blockType: BlockType.CODE,
            template: `
lcd_img_widgets(${i}).filename="${widgetObject.img.split('\n')[0]}"
lcd_img_widgets(${i}).x_pos=${widgetObject.x}
lcd_img_widgets(${i}).y_pos=${widgetObject.y}
lcd_img_widgets(${i}).x_offset=${widgetObject.x_offset || 0}
lcd_img_widgets(${i}).y_offset=${widgetObject.y_offset || 0}
lcd_img_widgets(${i}).width=${widgetObject.w}
lcd_img_widgets(${i}).height=${widgetObject.h}
lcd_img_widgets(${i}).visible=${widget.getSettingValue('VISIBLE') === '1' ? 'true' : 'false'}
lcd_img_widgets(${i}).screen=${widgetObject.screen}
`,
        });
    }

    for (let i = 0; i < textWidgets.length; i++) {
        const widget = textWidgets[i];
        const widgetObject = widget.getWidgetObject();


        if (widgetObject.font !== 'LCD_STD_FONT' && widgetObject.font !== 'LCD_SMALL_FONT') {
            const fontFormat = widgetObject.font.split('_')[1];
            const fontSizeValue = widgetObject.font.split('_')[2];

            blocks.push({
                location: 'GLOBAL_DEFINES',
                blockType: BlockType.CODE,
                id: widgetObject.font,
                template: `
#define ${widgetObject.font} "font_${fontFormat}_${fontSizeValue}.bin"
    `,
            });
        }


        blocks.push({
            location: 'lcd_init_widgets',
            blockType: BlockType.CODE,
            template: `
lcd_text_widgets(${i}).text="${widgetObject.text}"
lcd_text_widgets(${i}).x_pos=${widgetObject.x}
lcd_text_widgets(${i}).y_pos=${widgetObject.y}
lcd_text_widgets(${i}).width=${widgetObject.w}
lcd_text_widgets(${i}).height=${widgetObject.h}
lcd_text_widgets(${i}).forecolor=${PlatformUtils.hexToTibboColor(widgetObject.color)}
lcd_text_widgets(${i}).backcolor=${PlatformUtils.hexToTibboColor(widgetObject.backgroundColor)}
lcd_text_widgets(${i}).font=${widgetObject.font}
lcd_text_widgets(${i}).text_alignment=${widgetObject.alignment}
lcd_text_widgets(${i}).visible=${widget.getSettingValue('VISIBLE') === '1' ? 'true' : 'false'}
lcd_text_widgets(${i}).screen=${widgetObject.screen}
`,
        });
    }

    if ((feature as any).getSettingsEnabled()) {
        let tmpBlocks: Array<CodeBlock> = [];
        const groups = feature.getSettingValue('GROUPS');
        let sdfFile = '';
        let luisFile = '';
        let menuFile = '';
        for (let i = 0; i < groups.length; i += 1) {
            const group = groups[i];
            tmpBlocks = tmpBlocks.concat(group.getCodeBlocks());
        }
        for (let i = 0; i < tmpBlocks.length; i += 1) {
            switch (tmpBlocks[i].location) {
                case 'luis.xtxt':
                    luisFile += tmpBlocks[i].template;
                    break;
                case 'sdf.xtxt':
                    sdfFile += tmpBlocks[i].template;
                    break;
                case 'menu.xtxt':
                    menuFile += tmpBlocks[i].template;
                    break;
                default:
                    sdfFile += tmpBlocks[i].template;
                    luisFile += tmpBlocks[i].template;
                    break;
            }
        }


        if (groups.length === 1) {
            const lines = menuFile.split('\n');
            for (let i = 0; i < lines.length; i += 1) {
                if (lines[i].trim().length === 0) {
                    lines.splice(i, 1);
                    i -= 1;
                }
            }
            lines.shift();
            for (let i = 0; i < lines.length; i += 1) {
                // set menu level to 0
                lines[i] = `${lines[i].substr(0, 4)}0${lines[i].substr(5)}`;
            }
            menuFile = lines.join('\n');
        }

        blocks.push({
            location: 'on_sys_timer',
            blockType: BlockType.CODE,
            template: `
    ui_input_box_proc_timer()
                `,
        });

        blocks.push({
            location: 'on_kp',
            blockType: BlockType.CODE,
            template: `
        if ui_active=true then
            ui_handle_kp(key_event,key_code)
            if ui_active=false then
                exit sub
            end if
        end if`,
        });

        blocks.push({
            location: 'MENU_XTXT',
            blockType: BlockType.CODE,
            template: `
    Acc\tMen\tItem\tItem\tItem
    lvl\tlvl\ttype\tString\tData
    ${menuFile}\r\n`,
        });
    }

    blocks.push({
        location: 'DEVICE_TBS',
        blockType: BlockType.CODE,
        template: `
sub ui_menu_exit()
    '****ui_menu_exit****'
end sub`,
    });

    blocks.push({
        location: 'GLOBAL_DEFINES',
        blockType: BlockType.CODE,
        template: `
#define MENU_CURSOR_STYLE 1
#define LIST_BOX_CURSOR_STYLE 1
#define INPUT_BOX_CURSOR_STYLE 1
#define LCD_LOCK lcd.lock
#define LCD_UNLOCK lcd.unlock
#define VER_SPACING 9
#define HOR_SPACING 2 

#define BMP_LEFT_XOFFSET 320
#define BMP_NUM_XOFFSET 240
#define BMP_ALPH1_XOFFSET 280
#define BMP_ALPH2_XOFFSET 360
#define BMP_STORE_XOFFSET 40

#define STD_INPUT_WAIT_TIME 20 'in 0.5 seconds

'------ ui_menu ------
#define VER_SPACING 9
#define HOR_SPACING 2
#define BMP_CANCEL_XOFFSET 0
#define BMP_ENTER_XOFFSET 40
#define BMP_UP_XOFFSET 80
#define BMP_DOWN_XOFFSET 120
#define MENU_MAX_LVL 5
#define MENU_MAX_ITEM_PER_LVL 16
#define MENU_DISPLAY_ITEM_PER_LVL 6
#define LCD_MENU_TOUT 0
#define DISPLAY_STR_LEN 150
#define MENU_STRING_LEN 255
#define SCROLL_BAR_WIDTH 15
#define SCROLL_ARROW_HEIGHT 12
#define SCROLL_MIN_HEIGHT 2
#define SCROLL_CURSOR_WIDTH 9
#define CURSOR_BAR_LEN_OFFSET 2
#define SCROLL_BORDER 2
#define LCD_BACKCOLOR 0
#define LCD_FORECOLOR 65535
#define LCD_DISABLE_BACKCOLOR 14823
#define LCD_DISABLE_FORECOLOR 33840
#define LCD_CURSORCOLOR 63080
#define LCD_DEAD_SCREEN_COLOR 63488
#define MENU_X 0
#define MENU_Y 7
#define MENU_W 320
#define MENU_H 187
#define MENU_MAX_LINE 6
#define UM_HOR_SPACING HOR_SPACING
#define UM_VER_SPACING VER_SPACING
#define MENU_SCRIPT_FILE "menu.xtxt"
#define USE_STG_TIMESTAMP 0

'------ ui_msg_box ------
#define MSG_BOX_X 0
#define MSG_BOX_Y 0
#define MSG_BOX_W lcd.width
#define MSG_BOX_H 185
#define MSB_HOR_SPACING 2
#define MSB_VER_SPACING 3

'------ ui_input_box -----
#define IB_MAX_LEN 64
#define LCD_FIX_FONT "11X19(H).bin"
#define INPUT_BOX_X MENU_X
#define INPUT_BOX_Y MENU_H+MENU_Y-9
#define INPUT_BOX_W MENU_W-MENU_X
#define INPUT_BOX_H 25
#define INPUT_BOX_CURSOR_OFFSET 2
#define INPUT_BOX_CURSOR_WIDTH 2
#define INPUT_BOX_INIT_POS 0
#define LCD_INPUTBOX_FORECOLOR 65535
#define LCD_INPUTBOX_BACKCOLOR 63080
#define UIB_HOR_SPACING HOR_SPACING
#define RESTART yes

'------ ui_list_box ------
#define SMALL_ICON_FILE "small_icons.bmp"
#define LIST_BOX_X 25
#define LIST_BOX_Y 15
#define LIST_BOX_W 270
#define LIST_BOX_H 160
#define SCROLL_ARROW_X_POS_OFFSET 0
#define SCROLL_ARROW_Y_POS_OFFSET 2
#define ULB_VER_SPACING VER_SPACING
#define ULB_HOR_SPACING HOR_SPACING`,
    });

    return blocks;
};
